import { Input } from '../../../Components'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { CALL_TYPES, COMMUNICATION_CHANNELS } from '../constants'
import { CollapsibleSection } from './CollapsibleSection'
import { DebugToggle } from './DebugToggle'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Sezione configurazione esecuzione per la debug console.
 * Gestisce tipo chiamata, unit/processo, canale comunicazione, isDebug, pdfQuotes.
 * Layout 2 colonne per garantire visibilità completa dei valori.
 * @param {Object} props
 * @param {Object} props.config - Stato corrente della configurazione
 * @param {Function} props.setField - Funzione per aggiornare un campo
 * @param {Array} props.units - Lista delle unit/processi disponibili
 */
export function ExecutionSection({ config, setField, units }) {
  const selectedCallType = CALL_TYPES.find((ct) => ct.value === config.callType)

  return (
    <CollapsibleSection
      id="execution"
      title={t.dbcExecution || 'Esecuzione'}
      icon="pi pi-play"
      badge={
        <span className="nbadge nbadge--info">
          <i className="pi pi-wrench" style={{ fontSize: 11, marginRight: 4 }} />
          {selectedCallType?.sync ? 'Sync' : 'Async'}
        </span>
      }
    >
      <div className="debug-params-grid nora-test-params">
        <div className="nfield" style={{ gridColumn: 'span 6' }}>
          <label>{t.dbcCallType || 'Tipo Chiamata'}</label>
          <Input
            id="callType"
            dropdown
            options={CALL_TYPES}
            optionLabel="label"
            optionValue="value"
            value={config.callType}
            style={{ fontSize: 13, width: '100%', borderRadius: 8 }}
            onChange={({ callType }) => {
              setField('callType', callType)
              const meta = CALL_TYPES.find((ct) => ct.value === callType)
              if (meta?.channel) setField('communicationChannel', meta.channel)
            }}
          />
        </div>

        <div className="nfield" style={{ gridColumn: 'span 6' }}>
          <label>{t.dbcUnit || 'Unit / Processo'}</label>
          <Input
            id="unit"
            dropdown
            options={units}
            value={config.unit}
            filter
            showClear
            placeholder={t.dbcUnitPlaceholder || 'Default del tipo chiamata'}
            filterPlaceholder={t.dbcSearchUnit || 'Cerca unit...'}
            style={{ fontSize: 13, width: '100%', borderRadius: 8 }}
            onChange={({ unit }) => setField('unit', unit)}
          />
        </div>

        <div className="nfield" style={{ gridColumn: 'span 6' }}>
          <label>{t.dbcChannel || 'Canale Comunicazione'}</label>
          <Input
            id="communicationChannel"
            dropdown
            options={COMMUNICATION_CHANNELS}
            optionLabel="label"
            optionValue="value"
            value={config.communicationChannel}
            style={{ fontSize: 13, width: '100%', borderRadius: 8 }}
            onChange={({ communicationChannel }) =>
              setField('communicationChannel', communicationChannel)
            }
          />
        </div>

        <div
          className="nfield"
          style={{
            gridColumn: 'span 6',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            paddingTop: 18
          }}
        >
          {selectedCallType && (
            <span style={{ fontSize: 12, color: 'var(--ink-500)', lineHeight: 1.4 }}>
              <i className="pi pi-info-circle" style={{ marginRight: 4 }} />
              {selectedCallType.init ? 'Init: SI' : 'Init: NO'} · Channel default:{' '}
              <strong>{selectedCallType.channel || 'N/A'}</strong>
            </span>
          )}
        </div>

        <DebugToggle
          span="span 4"
          label={t.dbcIsDebug || 'Debug Mode (no DB)'}
          checked={config.isDebug}
          onChange={(value) => setField('isDebug', value)}
          hint={config.isDebug ? 'Nessuna scrittura su DB' : 'Conversazione persistita'}
        />

        <DebugToggle
          span="span 4"
          label={t.dbcSendEmail || 'Invia Email'}
          checked={config.sendEmail}
          onChange={(value) => setField('sendEmail', value)}
          onLabel="ON — invio reale"
          offLabel="OFF — solo chat"
          hint={config.sendEmail ? 'La mail parte davvero al destinatario' : undefined}
        />

        <DebugToggle
          span="span 4"
          label={t.dbcPdfQuotes || 'PDF Quotes'}
          checked={config.pdfQuotes}
          onChange={(value) => setField('pdfQuotes', value)}
          hint="Genera il PDF preventivo nel flusso"
        />
      </div>
    </CollapsibleSection>
  )
}
